import React, { useState, useEffect, useCallback } from 'react';
import { Table, Pagination, Spin, Checkbox, Space } from 'antd';
import type { TableProps } from 'antd';
import './ResponsiveTable.css';

const MOBILE_BREAKPOINT = 768;
const CARD_BREAKPOINT = 1024;

export interface ResponsiveTableProps<T = any> extends Omit<TableProps<T>, 'scroll'> {
  /** Card title field - dataIndex of the column used as card title */
  cardTitleField?: string;
  /** Fields to show in the card body (dataIndex list). If not provided, shows all non-action columns */
  cardFields?: string[];
  /** Custom card render function for full control */
  cardRender?: (record: T, index: number) => React.ReactNode;
  /** Scroll config for table mode (default: { x: 'max-content' }) */
  scroll?: TableProps<T>['scroll'];
}

function ResponsiveTable<T extends Record<string, any> = any>({
  columns = [],
  dataSource,
  loading,
  pagination,
  rowKey,
  rowSelection,
  cardTitleField,
  cardFields,
  cardRender,
  scroll,
  ...restProps
}: ResponsiveTableProps<T>) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < CARD_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < CARD_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine the title field: use provided cardTitleField, or first column
  const titleField = cardTitleField || (columns.length > 0 ? (columns[0] as any).dataIndex : undefined);

  // Determine which columns to display in the card body
  const getCardColumns = useCallback(() => {
    if (cardFields) {
      return (columns as any[]).filter((col) => cardFields.includes(col.dataIndex));
    }
    // By default: show all columns except action column and title column
    return (columns as any[]).filter(
      (col) => col.key !== 'action' && col.key !== 'actions' && col.dataIndex !== titleField
    );
  }, [columns, cardFields, titleField]);

  // Find the action column
  const actionColumn = (columns as any[]).find(
    (col) => col.key === 'action' || col.key === 'actions'
  );

  // Get title column for rendering
  const titleColumn = (columns as any[]).find((col) => col.dataIndex === titleField);

  // Render a cell value using column render function if available
  const renderCell = (col: any, record: T) => {
    const value = record[col.dataIndex];
    if (col.render) {
      return col.render(value, record, 0);
    }
    if (value === null || value === undefined) return '-';
    return String(value);
  };

  // Get the row key for a record
  const getRowKey = (record: T, index: number): React.Key => {
    if (typeof rowKey === 'function') {
      return rowKey(record, index);
    }
    if (typeof rowKey === 'string') {
      return record[rowKey];
    }
    return index;
  };

  // Handle selection for cards
  const handleSelectChange = (key: React.Key, checked: boolean) => {
    if (!rowSelection) return;
    const { selectedRowKeys = [], onChange } = rowSelection;
    const newKeys = checked
      ? [...selectedRowKeys, key]
      : selectedRowKeys.filter((k) => k !== key);
    onChange?.(newKeys, [], { type: 'single' } as any);
  };

  if (!isMobile) {
    // Desktop: render standard Ant Design Table
    return (
      <div className="apple-table-wrapper">
        <Table<T>
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={pagination}
          rowKey={rowKey}
          rowSelection={rowSelection}
          scroll={scroll || { x: 'max-content' }}
          {...restProps}
        />
      </div>
    );
  }

  // Mobile: render cards
  const records = (dataSource || []) as T[];
  const cardColumns = getCardColumns();

  return (
    <div className="responsive-card-list">
      {loading ? (
        <div className="responsive-card-loading">
          <Spin />
        </div>
      ) : records.length === 0 ? (
        <div className="responsive-card-empty">暂无数据</div>
      ) : (
        <>
          {records.map((record, index) => {
            const key = getRowKey(record, index);
            const isSelected = rowSelection?.selectedRowKeys?.includes(key);

            if (cardRender) {
              return (
                <div key={String(key)} className="responsive-card-item">
                  {cardRender(record, index)}
                </div>
              );
            }

            return (
              <div
                key={String(key)}
                className={`responsive-card-item${isSelected ? ' responsive-card-selected' : ''}`}
              >
                {/* Card Header */}
                <div className="responsive-card-header">
                  <div className="responsive-card-title">
                    {rowSelection && (
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleSelectChange(key, e.target.checked)}
                        className="responsive-card-checkbox"
                      />
                    )}
                    <span className="responsive-card-title-text">
                      {titleColumn ? renderCell(titleColumn, record) : record[titleField]}
                    </span>
                  </div>
                  {actionColumn && (
                    <div className="responsive-card-actions">
                      {actionColumn.render?.(undefined, record, index)}
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="responsive-card-body">
                  {cardColumns.map((col) => (
                    <div key={col.key || col.dataIndex} className="responsive-card-field">
                      <span className="responsive-card-label">{col.title}</span>
                      <span className="responsive-card-value">{renderCell(col, record)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination && (pagination as any) !== false && (
            <div className="responsive-card-pagination">
              <Pagination
                size="small"
                current={(pagination as any).current}
                pageSize={(pagination as any).pageSize}
                total={(pagination as any).total}
                showTotal={(t) => `共 ${t} 条`}
                showSizeChanger={(pagination as any).showSizeChanger}
                pageSizeOptions={(pagination as any).pageSizeOptions}
                onChange={(pagination as any).onChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResponsiveTable;
